import { useCallback, useEffect, useRef, useState } from 'react';

import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { omit, pick } from 'lodash-es';

import { toaster } from 'components/toaster/toaster';
import { IUserFormData, IUserInfoSubmitData } from 'components/user-info-form/types';
import {
  GetMeDocument,
  IUpdateUserDetailsInput,
  IUserDetailsFragment,
  useEditDeliveryAddressMutation,
  useGetMeQuery,
  useRemoveDeliveryAddressMutation,
  useSaveDeliveryAddressMutation,
  useUpdateMeMutation,
} from 'generated/graphql-gateway';
import {
  ICommunicationPreference,
  IDeliveryAddress,
  IEditDeliveryAddressInput,
  IFavoriteStore,
  IRemoveDeliveryAddressInput,
  ISaveDeliveryAddressInput,
} from 'generated/graphql-gateway';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { usePrevious } from 'hooks/use-previous';
import { updateUserAttributes as cognitoUpdateUserAttributes } from 'remote/auth/cognito';
import { checkForUnexpectedSignOut, getCurrentSession } from 'remote/auth/get-current-session';
import { subscribeToCurrentSession } from 'remote/auth/get-current-session';
import {
  CustomEventNames,
  EventTypes,
  logEvent,
  updateAmplitudeUserAttributes,
} from 'state/amplitude';
import { UpdateUserInfoOptions } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { updateLaunchDarklyUser } from 'state/launchdarkly/launchdarkly-core';
import { useNetworkContext } from 'state/network';
import { getName } from 'utils/attributes';
import { getCrashlytics, setUserId } from 'utils/crashlytics';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import logger, { addLoggingContext } from 'utils/logger';
import { useMemoAll } from 'utils/use-memo-all';

import { CommunicationPreferences, FavoriteStores, UserDetails } from './types';
import { useThirdPartyAuthentication } from './use-third-party-authentication';

export * from './types';

const configUserDetails = (details: IUserDetailsFragment) => ({
  dob: details.dob || '',
  email: details.email || '',
  emailVerified: details.emailVerified as boolean,
  name: details.name || '',
  phoneNumber: details.phoneNumber || '',
  phoneVerified: details.phoneVerified as boolean,
  promotionalEmails: details.promotionalEmails as boolean,
  isoCountryCode: details.isoCountryCode || '',
  zipcode: details.zipcode || '',
  defaultReloadAmt: details.defaultReloadAmt as number,
  defaultAccountIdentifier: details.defaultAccountIdentifier || '',
  defaultFdAccountId: details.defaultFdAccountId || '',
  defaultPaymentAccountId: details.defaultPaymentAccountId || '',
  autoReloadEnabled: details.autoReloadEnabled as boolean,
  autoReloadThreshold: details.autoReloadThreshold as number,
  loyaltyTier: details.loyaltyTier,
  communicationPreferences: (details.communicationPreferences as CommunicationPreferences) || null,
  favoriteStores: (details.favoriteStores as FavoriteStores) || null,
  deliveryAddresses: (details.deliveryAddresses as Array<IDeliveryAddress>) || null,
});

export const useCurrentUser = () => {
  const { isNetworkConnected } = useNetworkContext();
  const { logUserInToThirdPartyServices } = useThirdPartyAuthentication();
  const [currentUserSession, setCurrentUserSession] = useState<CognitoUserSession | null>(null);

  const { data: userData, loading, refetch, called } = useGetMeQuery({
    skip: !syncStorage.getString(SyncStorageKey.USER_ID),
    // Apollo client loading state gets stuck: https://github.com/apollographql/react-apollo/issues/3425
    // Temporary fix while we wait for a stable 3.0.0 version
    fetchPolicy: 'cache-and-network',
  });

  // NOTE: The updateMeMutation will attempt to refetch and update userData from useGetMeQuery
  // The refetchQueries query needs to match the useGetMeQuery exactly, including the variables
  const [updateMeMutation, { loading: useUpdateMeMutationLoading }] = useUpdateMeMutation({
    refetchQueries: [{ query: GetMeDocument }],
    awaitRefetchQueries: true,
  });

  const [saveDeliveryAddressMutation] = useSaveDeliveryAddressMutation();
  const [editDeliveryAddressMutation] = useEditDeliveryAddressMutation();
  const [removeDeliveryAddressMutation] = useRemoveDeliveryAddressMutation();

  useEffect(() => {
    getCurrentSession().then(user => {
      syncStorage.setString(SyncStorageKey.USER_ID, user?.getAccessToken().payload.username);
      setCurrentUserSession(user);
    });

    const unsubscribe = subscribeToCurrentSession(setCurrentUserSession);

    return () => void unsubscribe();
  }, []);

  const prevUserData = usePrevious(userData);

  // || null below to avoid big context switch during loading where .me is "undefined" instead of null
  const user = currentUserSession ? userData?.me || null : null;
  const cognitoId = user?.cognitoId ?? syncStorage.getString(SyncStorageKey.USER_ID) ?? null;

  const setCurrentUser = useCallback(
    async (session: null | CognitoUserSession) => {
      if (session && called) {
        await refetch();
      }
      setCurrentUserSession(session);
    },
    [called, refetch]
  );

  const refetchCurrentUser = useCallback(async () => {
    refetch();
  }, [refetch]);

  const refreshCurrentUser = useCallback(async () => {
    try {
      syncStorage.setString(
        SyncStorageKey.USER_ID,
        currentUserSession?.getAccessToken().payload.username
      );

      await setCurrentUser(currentUserSession);
    } catch (error) {
      logger.error({ error, message: 'An error occurred while refreshing the current user.' });
    }
  }, [currentUserSession, setCurrentUser]);

  const updateUserInfo = useCallback(
    async (form: IUserInfoSubmitData, options?: UpdateUserInfoOptions) => {
      try {
        // QUESTION - why do we await the current session here if we don't do anything with it?
        await getCurrentSession();
        cognitoUpdateUserAttributes(pick(form, 'phoneNumber', 'dob', 'name'));
        updateAmplitudeUserAttributes(form);
        updateLaunchDarklyUser(
          {
            key: user?.cognitoId,
            ...getName({ name: form.name }, { firstName: '', lastName: '' }),
            name: form.name,
            email: ((form as unknown) as IUserFormData).email,
          },
          'user info update'
        );

        const updateMeParams = omit(
          form,
          'agreesToTermsOfService',
          'defaultCheckoutPaymentMethodId',
          'defaultReloadPaymentMethodId',
          'deliveryAddresses',
          'email',
          'emailVerified',
          'phoneVerified'
        );

        const input = {
          ...updateMeParams,
          defaultAccountIdentifier: form.defaultCheckoutPaymentMethodId,
          defaultPaymentAccountId: form.defaultReloadPaymentMethodId,
        };

        await updateMeMutation({ variables: { input } });
      } catch (error) {
        if (!options) {
          return;
        }

        const errorMessage = 'Error: Update User Info Failure';

        if (!options.shouldMuteUserInfoErrors) {
          logger.error({ error, message: errorMessage });
          toaster.negative(t('updateInfoError'));
        } else {
          logger.error({
            error,
            message: `${errorMessage} - Muted`,
          });
        }
        if (options.shouldThrowException) {
          throw new Error(errorMessage);
        }
      }
    },
    [user?.cognitoId, updateMeMutation]
  );

  const saveDeliveryAddress = useCallback(
    async (
      input: ISaveDeliveryAddressInput,
      options?: { shouldThrowException?: boolean }
    ): Promise<void> => {
      try {
        await saveDeliveryAddressMutation({
          variables: { input },
          refetchQueries: [{ query: GetMeDocument }],
          awaitRefetchQueries: true,
        });
      } catch (error) {
        logger.error({
          error,
          message: 'Error: Save Delivery Address Failure',
        });

        if (options?.shouldThrowException) {
          throw new Error('Save Delivery Address Failure');
        }
      }
    },
    [saveDeliveryAddressMutation]
  );

  const editDeliveryAddress = useCallback(
    async (
      input: IEditDeliveryAddressInput,
      options?: { shouldThrowException?: boolean }
    ): Promise<void> => {
      try {
        await editDeliveryAddressMutation({
          variables: { input },
          refetchQueries: [{ query: GetMeDocument }],
          awaitRefetchQueries: true,
        });
      } catch (error) {
        logger.error({
          error,
          message: 'Error: Edit Delivery Address Failure',
        });

        if (options?.shouldThrowException) {
          throw new Error('Edit Delivery Address Failure');
        }
      }
    },
    [editDeliveryAddressMutation]
  );

  const removeDeliveryAddress = useCallback(
    async (
      input: IRemoveDeliveryAddressInput,
      options?: { shouldThrowException?: boolean }
    ): Promise<void> => {
      try {
        await removeDeliveryAddressMutation({
          variables: { input },
          refetchQueries: [{ query: GetMeDocument }],
          awaitRefetchQueries: true,
        });
      } catch (error) {
        logger.error({
          error,
          message: 'Error: Remove Delivery Address Failure',
        });

        if (options?.shouldThrowException) {
          throw new Error('Remove Delivery Address Failure');
        }
      }
    },
    [removeDeliveryAddressMutation]
  );

  const updateUserCommPrefs = useCallback(
    async (
      communicationPreferences: Array<ICommunicationPreference>,
      options?: { shouldThrowException?: boolean }
    ) => {
      const shouldThrowException = options?.shouldThrowException ?? false;
      try {
        const promotionalEmailsInput =
          (communicationPreferences.length && {
            promotionalEmails: communicationPreferences.some(({ value }) => value === 'true'),
          }) ||
          {};
        const input = {
          communicationPreferences,
          ...promotionalEmailsInput,
        };
        const { data } = await updateMeMutation({ variables: { input } });

        if (!data) {
          return logger.error({ message: 'An error occurred updating communication preference' });
        }

        const details = configUserDetails(data.updateMe.details);
        updateAmplitudeUserAttributes(details);
      } catch (error) {
        const errorMessage = 'Error: Update User Communication Preferences Failure';
        logger.error({ error, message: errorMessage });
        toaster.negative(t('updateInfoError'));
        if (shouldThrowException) {
          throw new Error(errorMessage);
        }
      }
    },
    [updateMeMutation]
  );

  const updateUserFavStores = useCallback(
    async (favoriteStores: Array<IFavoriteStore>) => {
      try {
        const input = { favoriteStores } as IUpdateUserDetailsInput;
        const { data } = await updateMeMutation({ variables: { input } });
        if (!data) {
          logger.error({ message: 'An error occurred updating favorite store' });
          toaster.negative(t('updateInfoError'));
        }
      } catch (error) {
        logger.error({ message: `An error occurred updating favorite store: ${error}` });
        toaster.negative(t('updateInfoError'));
      }
    },
    [updateMeMutation]
  );

  const checkIfUnexpectedSignOut = useCallback(async () => {
    const error = await checkForUnexpectedSignOut();
    if (error) {
      const isRefreshTokenExpired = /Refresh Token has expired/i.test(error.message);

      logger[isRefreshTokenExpired ? 'info' : 'error']({
        message: isRefreshTokenExpired ? 'Refresh token expired' : 'Unexpected Sign out',
        error,
      });
      // sent amplitude event telling that there has been an unexpected sign out
      logEvent(
        isRefreshTokenExpired
          ? CustomEventNames.REFRESH_TOKEN_EXPIRED
          : CustomEventNames.UNEXPECTED_SIGN_OUTS,
        EventTypes.Other,
        {
          cognitoId: prevUserData?.me?.cognitoId,
          error,
        }
      );
    }
  }, [prevUserData]);

  useEffect(() => {
    // decorate logger with cognito id
    addLoggingContext({ userId: cognitoId });
    // Add cognito id to crashlytics
    if (cognitoId) {
      setUserId(getCrashlytics(), cognitoId);
    }
  }, [cognitoId]);

  useEffect(() => {
    if (!user) {
      checkIfUnexpectedSignOut();
    }
  }, [checkIfUnexpectedSignOut, prevUserData, user]);

  useEffectOnUpdates(() => {
    // any update fetch + set current user
    if (isNetworkConnected) {
      refreshCurrentUser();
    }
  }, [isNetworkConnected]);

  // when user data populates for the first time (or logs in with a different user), we should sign them into all third party services as well
  const didThirdPartyLoginId = useRef<string>(undefined);
  useEffect(() => {
    if (userData && didThirdPartyLoginId.current !== userData?.me?.cognitoId) {
      logUserInToThirdPartyServices((userData.me as unknown) as UserDetails);
      didThirdPartyLoginId.current = userData?.me?.cognitoId;
    }
  }, [userData, logUserInToThirdPartyServices]);

  const isAuthenticated = !!currentUserSession;
  const userLoading = loading;

  const result = useMemoAll({
    refetchCurrentUser,
    refreshCurrentUser,
    setCurrentUser,
    updateUserCommPrefs,
    updateUserFavStores,
    updateUserInfo,
    saveDeliveryAddress,
    editDeliveryAddress,
    removeDeliveryAddress,
    user,
    isAuthenticated,
    currentUserSession,
    setCurrentUserSession,
    userLoading,
    useUpdateMeMutationLoading,
  });

  return result;
};
